import { useState } from 'react';
import { getContainer } from '@framework/di/getContainer';
import { getErrorMessage } from '@framework/message/Mapper/getMessage';
import { AlertManager } from '@dealroadshow/uikit-alert';
import LandingRepository from '@/dataroom/infrastructure/repository/LandingRepository';
import { useDataroomTenantContext } from '@dataroom/tenant/DataroomTenantContext';
import config from '@/dataroom/app/config/config';

export interface IDealVdrFormData {
  period: string,
  size: string,
  currency: string,
  company: string,
  firstName: string,
  lastName: string,
  email: string,
  phone: {
    code: string,
    value: string,
  },
  notes?: string,
  callback?: boolean,
  recaptcha: string,
}

export interface I17g5FormData {
  activeDatarooms: string,
  currentDataroomProvider: string,
  company: string,
  firstName: string,
  lastName: string,
  email: string,
  phone: {
    code: string,
    value: string,
  },
  notes?: string,
  callback?: boolean,
  recaptcha: string,
}

interface ISubmittedQuota {
  estimatedDuration: string,
  estimatedSize: string,
  companyName: string,
  fullName: string,
  userEmail: string,
  phoneNumber: string,
  quote: string,
  printLink: string,
}

const useGetQuote = () => {
  const container = getContainer();
  const [isFetching, setIsFetching] = useState<boolean>(false);
  const [submittedFormValues, setSubmittedFormValues] = useState<ISubmittedQuota | {}>({});
  const { tenant } = useDataroomTenantContext();

  const getDealvdrFormPayload = (formData: IDealVdrFormData) => ({
    periodId: formData.period,
    dataroomSizePlanId: +formData.size,
    currencyId: formData.currency,
    companyName: formData.company,
    firstName: formData.firstName,
    lastName: formData.lastName,
    corporateEmail: formData.email,
    phoneNumber: formData.phone.value,
    notes: formData.notes || '',
    isCallbackRequested: !!formData.callback,
    recaptchaToken: formData.recaptcha,
  });

  const get17g5FormPayload = (formData: I17g5FormData) => ({
    dataroomAmount: +formData.activeDatarooms,
    currentProvider: formData.currentDataroomProvider,
    companyName: formData.company,
    firstName: formData.firstName,
    lastName: formData.lastName,
    corporateEmail: formData.email,
    phoneNumber: formData.phone.value,
    notes: formData.notes || '',
    isCallbackRequested: !!formData.callback,
    recaptchaToken: formData.recaptcha,
  });

  const getQuote = async (formData: IDealVdrFormData | I17g5FormData) => {
    setIsFetching(true);

    try {
      const formPayload = (tenant === config.tenant.tenantDealvdr.code)
        ? getDealvdrFormPayload(formData as IDealVdrFormData)
        : get17g5FormPayload(formData as I17g5FormData);

      const landingRepository = container.get<LandingRepository>(LandingRepository);
      const submittedFormValues = await landingRepository.getQuote(formPayload);

      setSubmittedFormValues(submittedFormValues);
    } catch (error) {
      AlertManager.error(getErrorMessage(error));
    } finally {
      setIsFetching(false);
    }
  };

  const resetGetQuote = () => {
    setSubmittedFormValues({});
  };

  return {
    isFetching,
    submittedFormValues: submittedFormValues as ISubmittedQuota,
    getQuote,
    resetGetQuote,
  };
};

export default useGetQuote;
